import { computed, watch, type ComputedRef, type Ref } from 'vue'
import type { PDataTablePaginationType } from '../types'

export type UsePaginationReturn = {
  lastPage: ComputedRef<number>
  startIndex: ComputedRef<number>
  endIndex: ComputedRef<number>
  isFirstPage: ComputedRef<boolean>
  isLastPage: ComputedRef<boolean>
  goToFirstPage: () => void
  goToPrevPage: () => void
  goToNextPage: () => void
  goToLastPage: () => void
}

export const usePagination = (pagination: Ref<PDataTablePaginationType>): UsePaginationReturn => {
  const showsAllRows = computed<boolean>(() => pagination.value.rowsPerPage <= 0)

  const lastPage = computed<number>(() => {
    if (showsAllRows.value) {
      return 1
    }

    return Math.max(1, Math.ceil(pagination.value.rowsNumber / pagination.value.rowsPerPage))
  })

  const startIndex = computed<number>(() => {
    if (pagination.value.rowsNumber <= 0) {
      return 0
    }

    return (pagination.value.page - 1) * pagination.value.rowsPerPage + 1
  })

  const endIndex = computed<number>(() => {
    if (showsAllRows.value) {
      return pagination.value.rowsNumber
    }

    const value = pagination.value.page * pagination.value.rowsPerPage

    return Math.min(value, pagination.value.rowsNumber)
  })

  const isFirstPage = computed<boolean>(() => {
    return pagination.value.page <= 1
  })

  const isLastPage = computed<boolean>(() => {
    return pagination.value.page >= lastPage.value
  })

  const goToFirstPage = (): void => {
    pagination.value.page = 1
  }

  const goToPrevPage = (): void => {
    pagination.value.page = Math.max(1, pagination.value.page - 1)
  }

  const goToNextPage = (): void => {
    pagination.value.page = Math.min(lastPage.value, pagination.value.page + 1)
  }

  const goToLastPage = (): void => {
    pagination.value.page = lastPage.value
  }

  watch(
    () => pagination.value.rowsPerPage,
    () => (pagination.value.page = 1),
  )

  return {
    lastPage,
    startIndex,
    endIndex,
    isFirstPage,
    isLastPage,
    goToFirstPage,
    goToPrevPage,
    goToNextPage,
    goToLastPage,
  }
}
